import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Box, makeStyles, CircularProgress } from '@material-ui/core';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { showToast } from '@klpkonsern/ui';

import { PROSJEKT_TYPE } from '@klp/formgenerator/interfaces';
import { PROSJEKT_SKJEMA_TYPE_IDS } from '@klp/prosjekt/interfaces';
import {
    ActionButton,
    Block,
    KlpModal,
    IconName,
    Illustration,
} from '@klp/design';

import { getSuperofficeProsjektUrl } from '../../utils';
import { prosjektActions } from '../../store/prosjektSlice';
import { ProsjektRoutes } from '../../config';
import { useCreateProsjektProcessMutation } from '../../services/rtkApi';
import { SuperOfficeProsjektSelect } from './Form/SuperOfficeProsjektSelect';
import { ProsjektTypeSelect } from './Form/ProsjektTypeSelect';
import { StartStepChoose } from './Form/StartStepChoose';
import { ProsjektSkjemaTypeSelect } from './Form/ProsjektSkjemaTypeSelect';
import type { ISoProsjektDto } from '../../services/types';

const useStyles = makeStyles((theme) => ({
    buttonContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: theme.spacing(2),
    },
    modal: {
        maxWidth: '600px',
    },
}));

const createValidationSchema = () => {
    return Yup.object().shape({
        type: Yup.string().required(),
        startStep: Yup.string().required(),
        superOfficeProsjektId: Yup.string().required(),
        prosjektskjemaType: Yup.number().when('type', {
            is: (type: PROSJEKT_TYPE) => type === PROSJEKT_TYPE.LTT,
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.notRequired(),
        }),
    });
};

type CreateProsjektFormValues = {
    type?: PROSJEKT_TYPE;
    startStep?: string;
    superOfficeProsjektId?: string;
    selectedSuperOfficeProsjekt?: ISoProsjektDto | null;
    prosjektskjemaType?: number;
};

export const CreateProsjekt = () => {
    const { t } = useTranslation();
    const classes = useStyles();
    const history = useHistory();
    const [open, setOpen] = useState(false);
    const [createProject, { isLoading: createProjectIsLoading }] =
        useCreateProsjektProcessMutation();
    const dispatch = useDispatch();
    const { setShowSelectedProsjektSummary } = prosjektActions;

    const initialValues: CreateProsjektFormValues = {
        type: undefined,
        startStep: undefined,
        superOfficeProsjektId: '',
        selectedSuperOfficeProsjekt: null,
        prosjektskjemaType: undefined,
    };

    const handleSubmit = async (values: CreateProsjektFormValues) => {
        if (
            !values.type ||
            !values.superOfficeProsjektId ||
            !values.startStep
        ) {
            return;
        }

        await createProject({
            type: values.type,
            steg: Number(values.startStep),
            superOfficeProsjektId: values.superOfficeProsjektId,
            prosjektskjemaType: values.prosjektskjemaType ?? 0,
            rekkefolgenummer: 0, // Always start with first in sequence (0)
        })
            .unwrap()
            .then((d) => {
                showToast({
                    body: t('prosjekt.successfulOperation'),
                    type: 'success',
                });

                if (!d?.prosjektId || !d?.prosessInstansId) return;
                dispatch(setShowSelectedProsjektSummary(false));
                handleClose();
                history.push(
                    ProsjektRoutes.projectSchemaSingle(
                        d?.prosessInstansId,
                        d?.prosjektId,
                    ),
                );
            })
            .catch((e) => {
                showToast({
                    body: e?.data ?? t('general.error.message'),
                    type: 'error',
                });
                handleClose();
            });
    };

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
    };

    return (
        <Box>
            <Button
                endIcon={
                    <Illustration icon={IconName.PLUS} klpPalette="secondary" />
                }
                variant="contained"
                color="secondary"
                onClick={handleOpen}
            >
                {t('project.opprettProsjekt.opprettProsjektKnapp')}
            </Button>
            <KlpModal
                isOpen={open}
                onClose={handleClose}
                translation="project.opprettProsjekt.title"
                className={classes.modal}
            >
                <Formik
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={createValidationSchema}
                >
                    {({
                        values,
                        touched,
                        errors,
                        handleChange,
                        setFieldValue,
                    }) => (
                        <Form>
                            <ProsjektTypeSelect
                                value={values.type}
                                touched={touched.type}
                                error={errors.type}
                                onChange={(type) => {
                                    setFieldValue('startStep', null);
                                    setFieldValue(
                                        'prosjektskjemaType',
                                        undefined,
                                    );
                                    setFieldValue('type', type);
                                }}
                            />
                            {values.type && (
                                <StartStepChoose
                                    projectType={values.type}
                                    value={values.startStep}
                                    touched={touched.startStep}
                                    error={errors.startStep}
                                    onChange={(event) => {
                                        setFieldValue(
                                            'prosjektskjemaType',
                                            undefined,
                                        );
                                        handleChange(event);
                                    }}
                                />
                            )}
                            {values.type === PROSJEKT_TYPE.LTT && (
                                <ProsjektSkjemaTypeSelect
                                    value={values.prosjektskjemaType}
                                    touched={touched.prosjektskjemaType}
                                    error={
                                        typeof errors.prosjektskjemaType ===
                                        'string'
                                            ? errors.prosjektskjemaType
                                            : undefined
                                    }
                                    onChange={(value) => {
                                        const previousValue =
                                            values.prosjektskjemaType;

                                        setFieldValue(
                                            'prosjektskjemaType',
                                            value,
                                        );

                                        if (
                                            previousValue ===
                                                PROSJEKT_SKJEMA_TYPE_IDS.UNDERPROSJEKT &&
                                            value ===
                                                PROSJEKT_SKJEMA_TYPE_IDS.HOVEDPROSJEKT
                                        ) {
                                            setFieldValue(
                                                'superOfficeProsjektId',
                                                '',
                                            );
                                            setFieldValue(
                                                'selectedSuperOfficeProsjekt',
                                                null,
                                            );
                                        }
                                    }}
                                />
                            )}
                            <SuperOfficeProsjektSelect
                                key={`superoffice-${values.prosjektskjemaType ?? 'none'}`}
                                selectedProsjektSkjemaTypeId={
                                    values.prosjektskjemaType
                                }
                                value={values.selectedSuperOfficeProsjekt}
                                touched={touched.superOfficeProsjektId}
                                error={errors.superOfficeProsjektId}
                                onChange={(value) => {
                                    setFieldValue(
                                        'superOfficeProsjektId',
                                        value?.id || '',
                                    );
                                    setFieldValue(
                                        'selectedSuperOfficeProsjekt',
                                        value,
                                    );
                                }}
                            />
                            <Box className={classes.buttonContainer}>
                                <Block
                                    style={{
                                        cursor: createProjectIsLoading
                                            ? 'not-allowed'
                                            : 'pointer',
                                    }}
                                >
                                    <Button
                                        disabled={createProjectIsLoading}
                                        variant="contained"
                                        color="secondary"
                                        type="submit"
                                        startIcon={
                                            createProjectIsLoading ? (
                                                <CircularProgress
                                                    size={20}
                                                    color="inherit"
                                                />
                                            ) : undefined
                                        }
                                    >
                                        {t(
                                            'project.opprettProsjekt.opprettProsjektKnapp',
                                        )}
                                    </Button>
                                </Block>
                                <ActionButton
                                    actionType="external"
                                    translation="project.opprettProsjekt.goToSuperOffice"
                                    onClick={() =>
                                        window.open(
                                            getSuperofficeProsjektUrl(),
                                            '_blank',
                                        )
                                    }
                                />
                            </Box>
                        </Form>
                    )}
                </Formik>
            </KlpModal>
        </Box>
    );
};
